import React from 'react';
import { Nullable } from 'global';
import { useFormContext, useWatch } from 'react-hook-form';
import { generateEquipmentName } from 'modules/equipment';
import { ChargeAttachType } from 'modules/equipment/types';
import { ListingForm } from 'components/shared/TableListing/types';

import { BreachIndicator } from '../BreachIndicator';
import type { ChargeListingService } from '../../../../../lib';
import { QuoteCalcViewModel } from '../../../../../api/types';
import { InvoiceDescriptionCell } from './InvoiceDescriptionCell';
import { EquipmentDescriptionCell } from './EquipmentDescriptionCell';
import { quoteCalcMapper } from '../../../../../mappers/quoteCalcMapper';
import { Quote, QuoteChargeListingEntity, QuoteLineItemType } from '../../../../../types';

interface Props {
  rowIndex: number;
  quote: Nullable<Quote>;
  invoiceId: string;
  priceCalc: Nullable<QuoteCalcViewModel>;
  chargeService: ChargeListingService;
  onRemoveRow: VoidFunction;
  onEditEquipment: (id: string, rowIndex: number) => void;
  onRemoveEquipment: (id: string, rowIndex: number) => void;
}

export const DescriptionCell = ({
  rowIndex,
  quote,
  invoiceId,
  priceCalc,
  chargeService,
  onRemoveRow,
  onEditEquipment,
  onRemoveEquipment,
}: Props) => {
  const { control, getValues } = useFormContext<ListingForm<QuoteChargeListingEntity>>();
  const currentRow = useWatch({ control, name: `bodyFields.${rowIndex}` });

  if (!currentRow) return null;

  const { type, charge, equipment } = currentRow;

  const breach = quoteCalcMapper.findCalcRow(priceCalc, currentRow)?.breach ?? null;

  if (type === QuoteLineItemType.Equipment) {
    return (
      <EquipmentDescriptionCell
        equipment={equipment}
        renderBreachIndicator={<BreachIndicator breach={breach} />}
        onEditEquipment={id => onEditEquipment(id, rowIndex)}
        onRemoveEquipment={id => onRemoveEquipment(id, rowIndex)}
      />
    );
  }

  const scope = chargeService.resolveParentScope(currentRow, invoiceId) ?? {
    id: invoiceId,
    type: ChargeAttachType.Invoice,
  };

  // DR-10718: in the Internal / Dealer cost group, a line attached to a unit names that unit (deals can have several)
  const unitRow =
    scope.type === ChargeAttachType.Equipment && chargeService.isDealerCostRow(currentRow, quote)
      ? (getValues('bodyFields') ?? []).find(
          row => row.type === QuoteLineItemType.Equipment && row.equipment?.equipmentId === scope.id
        )
      : undefined;
  const unitName = unitRow?.equipment ? generateEquipmentName(unitRow.equipment) : undefined;

  return (
    <InvoiceDescriptionCell
      scope={scope}
      unitName={unitName}
      charge={charge!}
      rowIndex={rowIndex}
      onRemoveRow={onRemoveRow}
      chargeService={chargeService}
      renderBreachIndicator={<BreachIndicator breach={breach} />}
    />
  );
};
